import { getExamesRepository } from '../repositories';
import { ApiError } from './types';

export interface VariacaoItem {
  id: number;
  nome: string;
  exames_count: number;
}

export interface ExameClinicoItem {
  id: number;
  nome: string;
  obrigatorio: boolean;
  preco: number;
  laboratorial: boolean;
  variacao_exame_clinico_id: number; // ID da relação variacao_exame_clinico para vincular
}

export class VariacaoService {
  private static get repository() {
    return getExamesRepository();
  }

  /**
   * Lista todas as variações (atividades complementares) disponíveis.
   * Variações são globais e NÃO têm relacionamento com clínicas.
   * Apenas os exames complementares têm relacionamento com clínicas.
   * @returns Promise com array de variações
   */
  static async getVariacoes(): Promise<VariacaoItem[]> {
    try {
      return await this.repository.getVariacoes();
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Lista exames clínicos disponíveis para uma variação e clínica.
   * Filtra apenas exames que a clínica tem capacidade de realizar (tem preço cadastrado).
   * @param variacaoId - ID da variação (atividade complementar)
   * @param clinicaId - ID da clínica (filtra exames que a clínica pode realizar)
   * @returns Promise com array de exames clínicos com preços normalizados
   */
  static async getExamesClinicosPorVariacaoClinica(variacaoId: number, clinicaId: number): Promise<ExameClinicoItem[]> {
    try {
      // A API já calcula e filtra exames sem preço válido
      return await this.repository.getExamesClinicosPorVariacaoClinica(variacaoId, clinicaId);
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Vincula/desvincula exame clínico a um colaborador
   * @param colaboradorId - ID do colaborador
   * @param exameClinicoId - ID do exame clínico
   * @param variacaoId - ID da variação
   * @param clinicaId - ID da clínica (opcional, será buscada automaticamente se não fornecida)
   * @param vincular - true para vincular, false para desvincular
   * @param empresaId - ID da empresa (opcional, será buscado do localStorage se não fornecido)
   * @returns Promise com resultado da operação
   */
  static async vincularExameClinicoColaborador(
    colaboradorId: number,
    exameClinicoId: number,
    variacaoId: number,
    clinicaId: number | undefined,
    vincular: boolean,
    empresaId?: number
  ): Promise<any> {
    try {
      return await this.repository.vincularExameClinicoColaborador(
        colaboradorId,
        exameClinicoId,
        variacaoId,
        clinicaId,
        vincular,
        empresaId
      );
    } catch (error: any) {
      throw this.handleApiError(error);
    }
  }

  /**
   * Trata erros da API
   */
  private static handleApiError(error: any): ApiError {
    if (error.response?.data) {
      return {
        message: error.response.data.message || error.message || 'Erro na requisição',
        statusCode: error.response.status || error.statusCode || 500,
        error: error.response.data.error || error.error || 'Unknown Error',
      };
    }

    if (error.message) {
      return {
        message: error.message,
        statusCode: 500,
        error: 'Network Error',
      };
    }

    return {
      message: 'Erro de conexão com o servidor',
      statusCode: 500,
      error: 'Network Error',
    };
  }
}
